import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FolderOpen, Search, Files, Building, Package,
  CheckCircle, XCircle, Clock, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import dataroomService from '../services/dataroomService';
import { useDebounce } from '../hooks/useDebounce';
import { GridLoader } from '../components/CommonLoader';

const FileManagerLanding = () => {
  const navigate = useNavigate();
  const [datarooms, setDatarooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchDatarooms();
  }, [debouncedSearchTerm, selectedStatus]);

  const fetchDatarooms = async () => {
    try {
      setLoading(true);
      const params = {
        search: debouncedSearchTerm
      };
      
      // Only add filters if they're explicitly selected
      if (selectedStatus) {
        params.approval_status = selectedStatus;
      }
      
      const response = await dataroomService.getDatarooms(params);
      
      // Handle different response formats
      let datarooms = [];
      if (Array.isArray(response)) {
        datarooms = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        datarooms = response.data;
      } else if (response && response.datarooms && Array.isArray(response.datarooms)) {
        datarooms = response.datarooms;
      } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        datarooms = response.data.data;
      } else {
        // Last resort - check if response itself has dataroom properties
        if (response && typeof response === 'object') {
          // Check for common pagination response format
          const possibleKeys = ['items', 'results', 'records', 'list'];
          for (const key of possibleKeys) {
            if (response[key] && Array.isArray(response[key])) {
              datarooms = response[key];
              break;
            }
          }
        }
      }
      
      setDatarooms(datarooms);
    } catch (error) {
      toast.error('Failed to fetch datarooms');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const handleDataroomSelect = (dataroom) => {
    navigate(`/datarooms/${dataroom.uuid}/files`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">File Manager</h1>
          <p className="mt-2 text-gray-600">Select a dataroom to manage its files</p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search datarooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25rem'
            }}
          >
            <option value="">All Datarooms</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Datarooms Grid */}
        {loading ? (
          <GridLoader cards={6} />
        ) : datarooms.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No datarooms found</h3>
            <p className="mt-2 text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria' : 'Create a dataroom to get started'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {datarooms.map((dataroom) => (
              <motion.div
                key={dataroom.uuid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleDataroomSelect(dataroom)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      {dataroom.logo ? (
                        <img
                          className="h-12 w-12 rounded-lg object-cover"
                          src={dataroom.logo}
                          alt={dataroom.name}
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-gray-300 flex items-center justify-center">
                          <FolderOpen className="h-6 w-6 text-gray-600" />
                        </div>
                      )}
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">{dataroom.name}</h3>
                        <p className="text-sm text-gray-500">{dataroom.company_name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Building className="w-4 h-4 mr-2" />
                      {dataroom.organization_type}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Package className="w-4 h-4 mr-2" />
                      {dataroom.pricing_plan?.name || 'No plan'}
                    </div>
                    <div className="flex items-center text-sm">
                      {getStatusIcon(dataroom.approval_status)}
                      <span className="ml-2">{dataroom.approval_status}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button className="w-full px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center">
                      <Files className="w-4 h-4 mr-2" />
                      Manage Files
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Alert */}
        {datarooms.length > 0 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">File Management Info</h3>
                <p className="mt-1 text-sm text-blue-700">
                  Select a dataroom to manage its files. You can upload, organize, and manage files within each dataroom.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileManagerLanding;